import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  Box,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  Select,
  MenuItem,
  Card,
  Stack,
  Pagination,
  CircularProgress,
  Typography,
  Button,
} from '@mui/material';
import { GridView, TableRows, Tag, ViewTimeline } from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { useTheme, alpha } from '@mui/material/styles';

import QueryCard from 'src/pages/QueryPages/components/QueryCard';
import QueryTable from 'src/pages/QueryPages/components/QueryTable';
import QueryTimeline from 'src/pages/QueryPages/components/QueryTimeline';
import HashtagFilter from 'src/pages/QueryPages/components/HashtagFilter';
import SearchBar from 'src/components/SearchBar';
import { PATHS } from 'src/constants/config';
import { setViewMode } from 'src/store/slices/viewModeSlice';
import { expertApi } from 'src/api/expertApi';
import { queryBelongsToCompany } from 'src/utils/query';
import { useDecodedToken } from 'src/hooks/useDecodedToken';
import { toast } from 'react-hot-toast';

const QueryList = ({
  queries,
  allQueries,
  users,
  mutate,
  showDeadline = true,
  pageType,
  sortOption,
  onSortChange,
  page,
  pageCount,
  onPageChange,
  selectedHashtags,
  onHashtagsChange,
  currentSearchParams,
  isLoading: externalLoading = false,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const viewMode = useSelector((state) => state.viewMode.mode);
  const [searchTerm, setSearchTerm] = useState('');
  const [experts, setExperts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isHashtagFilterOpen, setIsHashtagFilterOpen] = useState(false);
  const { decodedToken, isExpert } = useDecodedToken();

  console.log(queries);

  useEffect(() => {
    const fetchExperts = async () => {
      setIsLoading(true);
      try {
        const response = await expertApi.getExperts();
        console.log('response', response);
        setExperts(response);
      } catch (error) {
        toast.error('Error fetching experts:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchExperts();
  }, []);

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      dispatch(setViewMode(newView));
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
  };

  const filterQueries = (query) => {
    const searchTermLower = searchTerm.toLowerCase();

    // First check if query belongs to user's company
    if (decodedToken?.company && experts.length > 0) {
      // Use the new utility function to check if query belongs to company
      const belongsToCompany = queryBelongsToCompany(experts, query, decodedToken.company);

      if (!belongsToCompany) {
        return false;
      }
    }

    // Check search term
    return (
      query.title.toLowerCase().includes(searchTermLower) ||
      query.description.toLowerCase().includes(searchTermLower) ||
      query.mediaOutlet.toLowerCase().includes(searchTermLower)
    );
  };

  // Use useMemo to avoid unnecessary filtering and track when filtering is complete
  const filteredQueries = useMemo(() => {
    // Don't filter if experts aren't loaded yet and we need company filtering
    if (decodedToken?.company && experts.length === 0) {
      return [];
    }

    return queries.filter(filterQueries);
  }, [queries, experts, decodedToken, searchTerm]);

  // Determine if we should show loading state
  const showLoading = externalLoading || isLoading || (decodedToken?.company && experts.length === 0);

  // Create query detail path with preserved search params
  const createQueryPath = (queryId) => {
    const basePath = PATHS.QUERY_PROFILE.value.replace(':queryID', queryId);
    return currentSearchParams ? `${basePath}?${currentSearchParams}` : basePath;
  };

  return (
    <>
      {!isExpert && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            gap: 2,
            px: 2,
            py: 1,
            bgcolor: 'white',
            borderRadius: 1,
            boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Box sx={{ flex: 1, height: 40, display: 'flex', alignItems: 'center' }}>
            <SearchBar onSearch={handleSearch} />
          </Box>
          <Button
            variant="outlined"
            startIcon={<Tag />}
            onClick={() => setIsHashtagFilterOpen(true)}
            sx={{
              height: 40,
              borderColor: alpha(theme.palette.primary.main, 0.2),
              color: theme.palette.text.primary,
              backgroundColor: theme.palette.background.paper,
              '&:hover': {
                borderColor: theme.palette.primary.main,
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
              },
              ...(selectedHashtags.size > 0 && {
                borderColor: theme.palette.primary.main,
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
              }),
            }}
          >
            Hashtags
            {selectedHashtags.size > 0 && (
              <Box
                sx={{
                  ml: 1,
                  minWidth: 20,
                  height: 20,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.primary.main,
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                }}
              >
                {selectedHashtags.size}
              </Box>
            )}
          </Button>
          <FormControl size="small" sx={{ height: 40, display: 'flex', justifyContent: 'center' }}>
            <Select
              value={sortOption}
              onChange={onSortChange}
              sx={{
                height: '100%',
                '&& .MuiSelect-select': {
                  bgcolor: 'white',
                  py: 1,
                },
              }}
            >
              <MenuItem value="pullTime">Pull time</MenuItem>
              <MenuItem value="dueDate">Due date</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            <ToggleButtonGroup value={viewMode} exclusive onChange={handleViewChange} aria-label="view mode" size="small">
              <ToggleButton value="grid" aria-label="grid view">
                <GridView />
              </ToggleButton>
              <ToggleButton value="table" aria-label="table view">
                <TableRows />
              </ToggleButton>
              <ToggleButton value="timeline" aria-label="timeline view">
                <ViewTimeline />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
      )}

      {showLoading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 10 }}>
          <CircularProgress size={40} />
          <Typography variant="body1" sx={{ mt: 2 }}>
            Loading queries...
          </Typography>
        </Box>
      ) : filteredQueries.length === 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 10 }}>
          <Typography variant="h6" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
            No queries found
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            {searchTerm ? 'Try adjusting your search criteria' : 'There are no queries to display at the moment'}
          </Typography>
        </Box>
      ) : viewMode === 'grid' ? (
        <Box>
          <Grid container spacing={2}>
            {filteredQueries.map((query) => (
              <Grid item xs={12} sm={6} md={3} key={query.id}>
                <QueryCard query={query} users={users} path={createQueryPath(query.id)} mutate={mutate} showDeadline={showDeadline} />
              </Grid>
            ))}
          </Grid>
          {filteredQueries?.length > 0 && pageCount > 1 && (
            <Stack justifyContent="center" alignItems="center" sx={{ mt: 2 }}>
              <Card sx={{ p: 2 }}>
                <Pagination count={pageCount} page={page} onChange={onPageChange} color="primary" />
              </Card>
            </Stack>
          )}
        </Box>
      ) : viewMode === 'timeline' ? (
        <QueryTimeline
          queries={filteredQueries}
          users={users}
          path={PATHS.QUERY_PROFILE.value}
          mutate={mutate}
          currentSearchParams={currentSearchParams}
          pageType={pageType}
        />
      ) : (
        <QueryTable
          queries={filteredQueries}
          users={users}
          path={PATHS.QUERY_PROFILE.value}
          mutate={mutate}
          pageType={pageType}
          currentSearchParams={currentSearchParams}
        />
      )}

      <HashtagFilter
        open={isHashtagFilterOpen}
        onClose={() => setIsHashtagFilterOpen(false)}
        queries={allQueries || queries}
        selectedHashtags={selectedHashtags}
        onHashtagsChange={onHashtagsChange}
      />
    </>
  );
};

QueryList.propTypes = {
  queries: PropTypes.array.isRequired,
  allQueries: PropTypes.array,
  users: PropTypes.array.isRequired,
  mutate: PropTypes.func.isRequired,
  showDeadline: PropTypes.bool,
  pageType: PropTypes.string.isRequired,
  sortOption: PropTypes.string.isRequired,
  onSortChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  pageCount: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  selectedHashtags: PropTypes.instanceOf(Set).isRequired,
  onHashtagsChange: PropTypes.func.isRequired,
  currentSearchParams: PropTypes.string,
  isLoading: PropTypes.bool,
};

export default QueryList;
